<template>
  <Toolbar v-if="user" class="p-mb-4 p-mt-4">
    <template #start>
      <div style="font-size: 1.4em;" class="p-mr-3" >{{ $store.getters.isForkliftTenant ? $t('Equipment') : $t('Cars') }}</div>
      <AddNewButton :disabled="customerIsLoading" :loading="customerIsLoading" :label="$t('Add')" @click="addNewItem"/>
    </template>
    <template #end>
      <SearchInput :disabled="dataTableIsLoading || customerIsLoading" @change-processed-search-value="changeProcessedSearchValue" v-model="searchData" @show-spinner="isLoading = true"/>
    </template>
  </Toolbar>
  <Spinner v-if="isLoading"/>
  <DesktopDataTable v-else
                    :dataTableIsLoading="dataTableIsLoading"
                    :itemsArrayIsEmpty="itemsArrayIsEmpty"
                    :sort="sort"
                    :showSearchInput="false"
                    :actionButtonsCount="actionButtonsCount"
                    @sort-items="sortItems"
                    :headers="[
              {name: 'car_make', title: $t('Make'), sortable: false},
              {name: 'car_model', title: $t('Model'), sortable: false},
              {name: 'plate_number', isHidden: $store.getters.isForkliftTenant, title: $t('Plate number'), sortable: false},
              // {name: 'vin', title: $t('VIN number'), sortable: true},
              {name: $store.getters.isForkliftTenant ? 'serial_number' : 'vin', title: $store.getters.isForkliftTenant ? $t('Serial Number') : $t('VIN number'), sortable: false},
              // {name: 'engine_code', title: $t('Engine code'), sortable: true},
              {name: $store.getters.isForkliftTenant ? 'asset_code' : 'engine_code', title: $store.getters.isForkliftTenant ? $t('Asset Code') : $t('Engine code'), sortable: false},
              {name: 'year', title: $t('Year'), sortable: true},
              {name: 'orders_count', class: 'ti-hummer', width: '70px', sortable: false},
              {name: 'offers_count', class: 'ti-clipboard', width: '70px', sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
    <template v-slot:body>
      <tr v-for="(item, index) of items"
          :key="index"
          :class="{'updated-item': isItemUpdated(item.id)}">
        <td>
          <span v-if="carMakeName(item)">{{ carMakeName(item) }}</span>
          <i v-else class="ti-minus"/>
        </td>
        <td>
          <span v-if="carModelName(item)">{{ carModelName(item) }}</span>
          <i v-else class="ti-minus"/>
        </td>
        <td v-if="!$store.getters.isForkliftTenant">
          <span v-if="item.plate_number">{{ item.plate_number }}</span>
          <i v-else class="ti-minus"/>
        </td>
        <td>
          <template v-if="$store.getters.isForkliftTenant">
            <span v-if="item.serial_number">{{ item.serial_number }}</span>
            <i v-else class="ti-minus"/>
          </template>
          <template v-else>
            <span v-if="item.vin">{{ item.vin }}</span>
            <i v-else class="ti-minus"/>
          </template>
        </td>
        <td>
          <template v-if="$store.getters.isForkliftTenant">
            <span v-if="item.asset_code">{{ item.asset_code }}</span>
            <i v-else class="ti-minus"/>
          </template>
          <template v-else>
            <span v-if="item.engine_code">{{ item.engine_code }}</span>
            <i v-else class="ti-minus"/>
          </template>
        </td>
        <td>
          <span v-if="item.year">{{ item.year }}</span>
          <i v-else class="ti-minus"/>
        </td>
        <td>
          <span>{{ item.ordersCount ?? 0 }}</span>
        </td>
        <td>
          <span>{{ item.offersCount ?? 0 }}</span>
        </td>
        <td class="table-date-line">
          {{ formatBy(item.created_at, item.creator) }}
        </td>
        <td v-show="actionButtonsCount">
          <div class="actions">
            <router-link :to="{ path: `/vehicles/${item.uid}` }"
                         draggable="false"
                         :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                         rel="noopener">
              <ViewButton :showSpinnerOnClick="false"/>
            </router-link>
            <EditButton :disabled="dataTableIsLoading || customerIsLoading"
                        @click="editItem(item)" />
            <DeleteButton v-show="canDelete"
                          :disabled="dataTableIsLoading || customerIsLoading"
                          @click="confirmItemDelete(item)"/>
          </div>
        </td>
      </tr>
    </template>
  </DesktopDataTable>

  <OwnPaginator v-show="!itemsArrayIsEmpty"
                :rows="pagination.perPage"
                :currentPage="pagination.currentPage - 1"
                :totalPages="pagination.pageCount"
                :totalRecords="pagination.totalCount"
                :rowsPerPageOptions="[5,10,25,50,100,200]"
                :pageLinkSize="11"/>

  <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

  <CarModal :visible="itemModal"
            :item="item"
            :userId="user?.id ? +user.id : null"
            @close="closeItemModal"
            @update-items="updateItems"/>

  <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                      :modalDataIsLoading="modalDataIsLoading"
                      @close="closeConfirmItemDeleteModal"
                      @delete-item="deleteItem"
                      :data="computedItemToDeleteData"/>

</template>

<script>
import httpClient from '@/services/http.services'
import CarModal from '@/pages/vehicles/components/CarModal'
import formatMixins from '@/mixins/formatMixins'
import datatableMixins from "@/mixins/datatableMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";

export default {
  mixins: [ formatMixins, showErrorsMixins, datatableMixins ],
  components: { CarModal },
  emits: ['update-items', 'change-customer-status'],
  props: {
    customerIsLoading: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    },
  },
	data() {
		return {
      fieldsExpand: 'creator,ordersCount,offersCount',
      apiLink: 'car/get-customer-active-cars',
      deleteItemUrl: 'car/delete',
      updatedItemId: null,
		}
	},
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('customers') === -1 || route.indexOf('vehicles') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
  },
  mounted() {
    // if (this.$store.state.user?.role || !this.$route.params.id) return false
    // if (!this.$route.params.id) return
    if (!this.$route.params.uid) return
    this.checkRouteQuery()
    // if (!this.$store.state.user?.role) return
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (!this.user?.id) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('customerCars')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          data: { userId: this.user.id },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.disableEditButton = false
        this.dataTableIsLoading = false
        if (this._abortors?.customerCars === ctrl) {
          this._abortors.customerCars = null
        }
      }
    },
	},
  computed: {
    canDelete() {
      return this.$store.getters.isAdminOrHigher
    },
    actionButtonsCount() {
      if (this.canDelete) {
        return 3
      } else if (this.$store.getters.isModeratorOrHigher) {
        return 2
      } else {
        return 1
      }
    },
    computedItemToDeleteData() {
      if (!this.itemToDelete) return null
      return this.formatCarLabel(this.itemToDelete)
    },
  }
}
</script>